import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';

// styles
import styles from './PdpRespHeroCarousel.scss';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'react-intl';
import LazyLoad from 'react-lazy-load';
import PdpRespZoomModalLoader from '../PdpRespZoomModal/PdpRespZoomModalLoader';
import PdpRespHeroCarouselRequestMoreImages from './PdpRespHeroCarouselRequestMoreImages';
import PdpRespHeroCarouselViewInRoom from './PdpRespHeroCarouselViewInRoom';

// helpers
import classnames from 'classnames';
import {
    trackClickCarousel,
    trackChangeCarousel,
    trackViewRoomDisplayed,
    trackViewRoomClick,
} from './heroCarouselTracking';

// messages
const counter = (index, totalItems) => (
    <FormattedMessage
        id="PdpRespHeroCarousel.counter"
        defaultMessage="{position, number} of {totalItems, number} images"
        values={{ position: index + 1, totalItems }}
    />
);

export class PdpRespHeroCarousel extends React.Component {
    constructor(props) {
        super(props);

        this.onRoomClick = this.onRoomClick.bind(this);
        this.onImageClick = this.onImageClick.bind(this);
        this.onImageChange = this.onImageChange.bind(this);
        this.onModalCloseClick = this.onModalCloseClick.bind(this);
        this.renderCarouselItem = this.renderCarouselItem.bind(this);

        this.setInstanceProperties(props);
        this.state = {
            index: 0,
            modalOpen: false,
            startingTab: 'images',
        };
    }
    componentWillReceiveProps(nextProps) {
        this.setInstanceProperties(nextProps);
    }
    setInstanceProperties({ item = {}, viewer }) {
        const { photos = [] } = item;
        this._images = photos.map(({ largePath: src, alt }) => ({ src, alt }));
        this._imagesZoom = photos.map(({ masterOrZoomPath: src }) => ({ src }));

        this._totalItems = get(viewer, 'isRequestMoreImagesEnabled')
            ? photos.length + 1
            : photos.length;
    }
    /**
     * Handler for when close 'x' is clicked (or ESC is pressed)
     */
    onModalCloseClick({ index }) {
        this.setState({ index, modalOpen: false });
    }
    /**
     * Handler for when image is clicked
     *
     * @param {number} index - clicked index
     */
    onImageClick({ index }) {
        this.setState({ modalOpen: true, startingTab: 'images' }, () => {
            // fire tracking
            trackClickCarousel({
                index,
                total: this._totalItems,
            });
        });
    }
    /**
     * Handler for when room is clicked
     */
    onRoomClick({ index }) {
        this.setState({ index: 0, modalOpen: true, startingTab: 'room' }, () => {
            trackViewRoomClick({
                index,
                total: this._totalItems,
            });
        });
    }
    /**
     * Handler for when image is changed
     *
     * @param {number} index - new index
     * @param {string} change - either 'previous' or 'next'
     */
    onImageChange({ change, index }) {
        const { item } = this.props;
        const total = this._totalItems;
        const canShowViewInRoom = get(item, 'canShowViewInRoom');

        this.setState({ index }, () => {
            trackChangeCarousel({
                index,
                total,
                next: change === 'next',
            });
            if (index + 1 === total && canShowViewInRoom) {
                trackViewRoomDisplayed({ index, total });
            }
        });
    }
    renderCarouselItem({ index, isCurrentItem }) {
        const position = index + 1;
        const { item, user, disableZoomModal, disableRequestMoreImagesModal, viewer } = this.props;
        const { src, alt } = this._images[index] || {};
        const isRequestMoreImagesEnabled = get(viewer, 'isRequestMoreImagesEnabled');
        const isCarouselLastItem = position === this._totalItems;
        const isRequestMoreImages = isCarouselLastItem && isRequestMoreImagesEnabled;
        const canShowViewInRoom = get(item, 'canShowViewInRoom');

        const imageClasses = classnames(styles.photo, { [styles.currentPhoto]: isCurrentItem });
        const wrapperProps = {
            className: classnames(styles.item, {
                [styles.moreImages]: isRequestMoreImages,
                'is-zoom': !isRequestMoreImages,
            }),
            onClick:
                isCarouselLastItem || disableZoomModal
                    ? () => ({})
                    : () => this.onImageClick({ index }),
        };

        const img = <img className={imageClasses} src={src} alt={alt} />;

        // Don't want to lazy load 1st image
        let imgEl =
            index === 0 ? (
                img
            ) : (
                <Fragment>
                    <LazyLoad className={styles.lazyLoad} offsetHorizontal={300} key={index}>
                        {img}
                    </LazyLoad>
                    <noscript
                        dangerouslySetInnerHTML={{
                            __html: `<img src="${src}" alt="${alt}" />`,
                        }}
                    />
                </Fragment>
            );

        if (isCarouselLastItem) {
            if (canShowViewInRoom) {
                imgEl = (
                    <PdpRespHeroCarouselViewInRoom onClick={() => this.onRoomClick({ index })} />
                );
            } else if (isRequestMoreImagesEnabled) {
                imgEl = (
                    <PdpRespHeroCarouselRequestMoreImages
                        disableRequestMoreImagesModal={disableRequestMoreImagesModal}
                        item={item}
                        user={user}
                    />
                );
            }
        }

        return (
            <div {...wrapperProps} data-tn={`pdp-image-carousel-image-${position}`}>
                {imgEl}
            </div>
        );
    }
    render() {
        const {
            itemTitle,
            itemImage,
            user,
            userId,
            itemId,
            item,
            viewer,
            fetchFavorites,
        } = this.props;
        const { index, modalOpen, startingTab } = this.state;

        return (
            <div className={`${styles.wrapper} enable-click-on-preview`}>
                {/* Zoom Modal */}
                <PdpRespZoomModalLoader
                    item={this.props.item}
                    user={this.props.user}
                    totalItems={this._totalItems}
                    onCloseClick={this.onModalCloseClick}
                    isOpen={modalOpen}
                    startingIndex={index}
                    startingTab={startingTab}
                    zoomImages={this._imagesZoom.map(({ src }) => src)}
                    itemTitle={itemTitle}
                    itemImage={itemImage}
                    itemId={itemId}
                    fetchFavorites={fetchFavorites}
                    viewer={viewer || null}
                />
                {/* Images */}
                <Carousel
                    itemKey="hero-item"
                    dataTn="pdp-image-carousel"
                    index={index}
                    totalItems={this._totalItems}
                    enableKeybordControl={!modalOpen} // disable arrows if modal is open
                    onIndexChange={this.onImageChange}
                    renderItem={this.renderCarouselItem}
                />
                {/* Counter */}
                <span className={styles.counter} data-tn="pdp-image-carousel-count-text">
                    {counter(index, this._totalItems)}
                </span>
            </div>
        );
    }
}

PdpRespHeroCarousel.defaultProps = {
    disableZoomModal: false,
    disableRequestMoreImagesModal: false,
};

PdpRespHeroCarousel.propTypes = {
    item: PropTypes.object.isRequired,
    user: PropTypes.object,
    itemId: PropTypes.string.isRequired,
    itemImage: PropTypes.string,
    itemTitle: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object,
    fetchFavorites: PropTypes.bool,
    disableZoomModal: PropTypes.bool.isRequired,
    disableRequestMoreImagesModal: PropTypes.bool.isRequired,
};

export default createFragmentContainer(PdpRespHeroCarousel, {
    item: graphql`
        fragment PdpRespHeroCarousel_item on Item {
            ...PdpRespHeroCarouselRequestMoreImages_item
            ...PdpRespZoomModal_item
            title
            photos {
                largePath
                masterOrZoomPath
                alt
            }
            canShowViewInRoom
        }
    `,
    user: graphql`
        fragment PdpRespHeroCarousel_user on User {
            ...PdpRespHeroCarouselRequestMoreImages_user
            ...PdpRespZoomModal_user
        }
    `,
    viewer: graphql`
        fragment PdpRespHeroCarousel_viewer on Viewer {
            ...PdpRespZoomModal_viewer
            isRequestMoreImagesEnabled: isEnabledByLocale(featureName: "REQUEST_MORE_IMAGES")
        }
    `,
});
